import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getTreeStartingPid } from '@fs/zion-tree-groups'

const StartingPersonContext = createContext()

/**
 *
 * @returns {{
 * loading: boolean
 * startingPersonId?: string
 * setStartingPersonId: import('react').Dispatch<import('react').SetStateAction<string>>
 * refetchStartingPersonPid: () => void
 * }}
 */

export const useStartingPerson = () => useContext(StartingPersonContext)

/**
 *
 * @param {{
 * treeId?: string
 * types?: import('@fs/zion-tree-types').GroupType[]
 * children: import('react').ReactNode
 * }} props
 * @returns {JSX.Element}
 */

export default function StartingPersonProvider({ treeId, types, children }) {
  const [startingPersonId, setStartingPersonId] = useState()
  const [loading, setLoading] = useState(true)
  const isTypeTree = types?.includes('PRIVATE_PERSON_SHARING') || types?.includes('RESEARCHER_TREE')
  const lastTreeStartingPersonDataWasFetched = useRef(false)
  const isCurrent = useRef()

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  const refetchStartingPersonPid = useCallback(() => {
    lastTreeStartingPersonDataWasFetched.current = false
  }, [])

  if (treeId && lastTreeStartingPersonDataWasFetched.current !== treeId) {
    lastTreeStartingPersonDataWasFetched.current = treeId
    const noCache = true

    getTreeStartingPid(treeId, noCache)
      .then(({ personId }) => {
        if (isCurrent.current && personId && isTypeTree) {
          setStartingPersonId(personId)
        }

        return undefined
      })
      .catch(console.error)
      .finally(() => isCurrent.current && setLoading(false))
  }

  const value = useMemo(
    () => ({ loading, startingPersonId, setStartingPersonId, refetchStartingPersonPid }),
    [refetchStartingPersonPid, startingPersonId, loading]
  )
  return <StartingPersonContext.Provider value={value}>{children}</StartingPersonContext.Provider>
}
